import React, {useRef, useState} from "react";
import {Helmet} from "react-helmet";
import {IGlobalDataContext, withGlobalData} from "./contexts/global-data";
import {CSVLink} from "react-csv";
import {Product, ProductMapping} from "./models";
import {getAPI, ProductServiceAttributeApi} from "./api";
import moment from "moment";
import formatUtilPrice from "./format_until_price";

interface Props extends IGlobalDataContext {
}

interface ExportRow {
    vmid: string;
    vendingMachineName: string;
    vendingMachineAttribute1: string;
    vendingMachineAttribute2: string;
    vendingMachineAttribute3: string;
    vendingMachineAttribute4: string;
    mdbCode: string;
    productName: string;
    productNameAttribute1: string;
    productNameAttribute2: string;
    productNameAttribute3: string;
    productNameAttribute4: string;
    productPrice: string;
    currentStock: number;
    capacity: number;
    lastTransactionTime: string;
    lastTransactionTimeUtc: string;
    longestShelfTime: string;
}

type GenerateStatus = "idle" | "generating" | "generated";

function _stockReport(props: Props) {
    const [exportRows, setExportRows] = useState<Array<ExportRow>>([]);
    const [generateStatus, setGenerateStatus] = useState<GenerateStatus>("idle");
    const [finishedBatches, setFinishedBatches] = useState<number>(0);
    const [totalBatches, setTotalBatches] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const csvLinkRef = useRef<CSVLink & HTMLAnchorElement>(null);
    const productServiceAttributeApi = new ProductServiceAttributeApi();

    const BATCH_SIZE = 7;


    async function retryApiCall<T>(fn: () => Promise<T>, retries: number = 2): Promise<T> {
        for (let i = 0; i < retries; i++) {
            try {
                return await fn();
            } catch (error) {
                if (i === retries - 1) throw error;
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
        }
        throw new Error("Max retries reached");
    }

    async function loadProductAttributes() {
        const products = props.allProducts;
        try {
            for (let i = 0; i < products.length; i += BATCH_SIZE) {
                const batch = products.slice(i, i + BATCH_SIZE);
                const productPromises = batch.map(async (product) => {
                    try {
                        const res = await retryApiCall(() =>
                            productServiceAttributeApi.productServiceGetProductAttributes(product.uuid)
                        );
                        product.attribute1 = res.data.attribute1 || "";
                        product.attribute2 = res.data.attribute2 || "";
                        product.attribute3 = res.data.attribute3 || "";
                        product.attribute4 = res.data.attribute4 || "";
                        return {success: true};
                    } catch (error) {
                        console.error(`Failed to get attributes for product ${product.uuid}:`, error);
                        return {success: false};
                    }
                });
                const results = await Promise.all(productPromises);
                const hasErrors = results.some((result) => !result.success);
                if (hasErrors) {
                    setErrorMessage("Some product attributes could not be loaded. The report may be incomplete.");
                }
                incrFinishedBatches();
            }
        } catch (error) {
            console.error("Unexpected error during batch get products attributes processing:", error);
            setErrorMessage("Failed to load product attributes. Please try again later.");
            throw error;
        }
    }

    async function loadVendingMachinesCoils() {
        const vendingMachines = props.allVendingMachines.allVendingMachines;
        let vendingMachinesCoilsMap: { [key: string]: Array<ProductMapping> } = {};
        try {
            for (let i = 0; i < vendingMachines.length; i += BATCH_SIZE) {
                const batch = vendingMachines.slice(i, i + BATCH_SIZE);
                const getCoilsPromises = batch.map(async (vendingMachine) => {
                    try {
                        const response = await retryApiCall(() =>
                            getAPI(`/api/vending_machines/${vendingMachine.uuid}/stock`)
                        );
                        vendingMachinesCoilsMap[vendingMachine.uuid] = response.data.productMaps;
                        return {success: true};
                    } catch (error) {
                        console.error(`Failed to load coils for vending machine ${vendingMachine.uuid}:`, error);
                        return {success: false};
                    }
                });
                const results = await Promise.all(getCoilsPromises);
                const hasErrors = results.some((result) => !result.success);
                if (hasErrors) {
                    setErrorMessage("Some vending machine coils could not be loaded. The report may be incomplete.");
                }
                incrFinishedBatches();
            }
            return vendingMachinesCoilsMap;
        } catch (error) {
            console.error("Unexpected error during batch get coils processing:", error);
            setErrorMessage("Failed to load vending machine coils. Please try again later.");
            throw error;
        }
    }

    function incrFinishedBatches() {
        setFinishedBatches((prev) => {
            return prev + 1;
        });
    }

    async function handleGenerateReport() {
        try {
            if (generateStatus === "generating") {
                setErrorMessage("A report is already being generated.");
                return;
            }

            if (!props.allProducts.length || !props.allVendingMachines.allVendingMachines.length) {
                setErrorMessage("No products or vending machines available to generate the report.");
                return;
            }

            setGenerateStatus("generating");
            setFinishedBatches(0);
            setErrorMessage(null);
            setExportRows([]);

            const productBatches = Math.ceil(props.allProducts.length / BATCH_SIZE);
            const vendingMachineBatches = Math.ceil(props.allVendingMachines.allVendingMachines.length / BATCH_SIZE);
            setTotalBatches(productBatches + vendingMachineBatches);

            await loadProductAttributes();
            const vendingMachinesCoilsMap = await loadVendingMachinesCoils();

            const defaultProduct = {
                uuid: "",
                id: "",
                attribute1: "",
                attribute2: "",
                attribute3: "",
                attribute4: "",
            } as Product;


            const nowTime = moment();

            let rows: ExportRow[] = [];
            props.allVendingMachines.allVendingMachines.forEach((vendingMachine) => {
                const coils = vendingMachinesCoilsMap[vendingMachine.uuid] || [];
                coils.forEach((coil: ProductMapping) => {
                    const product = findProductById(coil.product_id) || defaultProduct;
                    const formattedPrice = formatUtilPrice(coil.price, props.me.mostRecentCompany.currencyDecimalPoints);

                    let lastTransactionTime = "";
                    let lastTransactionTimeUtc = "";
                    const timezone = props.me.timezone || "UTC";

                    if (coil.lastTransactionTimestamp) {
                        const transactionTimestamp = moment.unix(coil.lastTransactionTimestamp);
                        lastTransactionTime = transactionTimestamp.tz(timezone).format("YYYY-MM-DD HH:mm:ss");
                        lastTransactionTimeUtc = transactionTimestamp.utc().format("YYYY-MM-DD HH:mm:ss");
                    }

                    let longestShelfTime = "";

                    if (coil.coilShelfTimes && coil.coilShelfTimes?.length > 0) {
                        longestShelfTime = nowTime.diff(moment.unix(coil.coilShelfTimes[0].timestamp), 'days').toString()
                    }

                    const row: ExportRow = {
                        vmid: vendingMachine.uuid,
                        vendingMachineName: vendingMachine.name,
                        vendingMachineAttribute1: vendingMachine.vendingMachineAttributes.attribute_1 || "",
                        vendingMachineAttribute2: vendingMachine.vendingMachineAttributes.attribute_2 || "",
                        vendingMachineAttribute3: vendingMachine.vendingMachineAttributes.attribute_3 || "",
                        vendingMachineAttribute4: vendingMachine.vendingMachineAttributes.attribute_4 || "",
                        mdbCode: coil.mdb_code,
                        productName: coil.product_name || "",
                        productNameAttribute1: product.attribute1,
                        productNameAttribute2: product.attribute2,
                        productNameAttribute3: product.attribute3,
                        productNameAttribute4: product.attribute4,
                        productPrice: formattedPrice,
                        currentStock: coil.currentStock || 0,
                        capacity: coil.capacity,
                        lastTransactionTime: lastTransactionTime,
                        lastTransactionTimeUtc: lastTransactionTimeUtc,
                        longestShelfTime: longestShelfTime,
                    };

                    rows.push(row);
                });
            });

            if (rows.length === 0) {
                setErrorMessage("No data available to generate the report.");
                setGenerateStatus("idle");
                return;
            }

            setExportRows(rows);
            setGenerateStatus("generated");
        } catch (error) {
            console.error("Failed to generate report:", error);
            setErrorMessage("An error occurred while generating the report. Please try again.");
            setGenerateStatus("idle");
            setFinishedBatches(0);
        }
    }

    function findProductById(id: string) {
        return props.allProducts.find((product) => product.id === id) || null;
    }

    function prepareCsvHeader() {
        return [
            {label: "Vmid", key: "vmid"},
            {label: "Vending Machine Name", key: "vendingMachineName"},
            {label: "Vending Machine Attribute 1", key: "vendingMachineAttribute1"},
            {label: "Vending Machine Attribute 2", key: "vendingMachineAttribute2"},
            {label: "Vending Machine Attribute 3", key: "vendingMachineAttribute3"},
            {label: "Vending Machine Attribute 4", key: "vendingMachineAttribute4"},
            {label: "Mdb Code", key: "mdbCode"},
            {label: "Product Name", key: "productName"},
            {label: "Product Attribute 1", key: "productNameAttribute1"},
            {label: "Product Attribute 2", key: "productNameAttribute2"},
            {label: "Product Attribute 3", key: "productNameAttribute3"},
            {label: "Product Attribute 4", key: "productNameAttribute4"},
            {label: "Product Price", key: "productPrice"},
            {label: "Current Stock", key: "currentStock"},
            {label: "Capacity", key: "capacity"},
            {label: "Last Transaction Time", key: "lastTransactionTime"},
            {label: "Last Transaction Time (UTC)", key: "lastTransactionTimeUtc"},
            {label: "Longest Shelf Time(days)", key: "longestShelfTime"},
        ];
    }

    function prepareCsvData() {
        return exportRows;
    }

    return (
        <div>
            <Helmet titleTemplate="%s - Vending on Track">
                <title>Stock Report</title>
            </Helmet>
            <section className="content">
                <div className="box statsBox">
                    <div className="box-header">
                        <h1>Stock Report</h1>
                    </div>
                    <div className="box-body">
                        <section className="content">
                            <div className="box box-success">
                                <div className="box-body with-border">
                                    <div className="form-group">
                                        <button
                                            onClick={handleGenerateReport}
                                            disabled={generateStatus === "generating"}
                                            className="btn btn-success"
                                        >
                                            Generate report
                                        </button>
                                        {generateStatus === "generated" && exportRows.length > 0 && (
                                            <CSVLink
                                                ref={csvLinkRef}
                                                headers={prepareCsvHeader()}
                                                data={prepareCsvData()}
                                                filename={"vending_machine_stocks_" + moment().unix() + ".csv"}
                                                className="btn btn-default"
                                                style={{marginLeft: "10px"}}
                                            >
                                                Download As CSV
                                            </CSVLink>
                                        )}
                                    </div>
                                    {errorMessage && (
                                        <div className="alert alert-danger" style={{marginTop: "10px"}}>
                                            {errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {generateStatus !== "idle" && (
                                <div style={{marginTop: "10px", display: "flex", flexDirection: "column"}}>
                                    <label>Generating: {(finishedBatches / totalBatches * 100).toFixed(0)}%</label>
                                    <progress
                                        value={finishedBatches / totalBatches * 100}
                                        max={100}
                                        style={{width: "300px"}}
                                    />
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            </section>
        </div>
    );
}

const StockReport = withGlobalData(_stockReport);
export default StockReport;